import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	ApproveRequest,
	RejectRequest,
} from "@/domain/eventInstance.interface";
import { eventInstanceService } from "../../services/eventInstance.service";

const QK_PENDING = ["event-instances", "pending-review"] as const;
const QK_FOR_ENROLLMENT = (
	enrollmentId: string,
	entryMethod?: "Manual" | "Automatic",
) =>
	[
		"event-instances",
		"enrollment",
		enrollmentId,
		entryMethod ?? "all",
	] as const;
const QK_DETAIL = (enrollmentId: string, instanceId: string) =>
	["event-instances", enrollmentId, instanceId, "review-detail"] as const;

export const usePendingReviewQueue = (options?: {
	refetchIntervalMs?: number;
}) => {
	return useQuery({
		queryKey: QK_PENDING,
		queryFn: () => eventInstanceService.listPendingReview(),
		refetchInterval: options?.refetchIntervalMs,
	});
};

export const useEnrollmentEventInstances = (
	enrollmentId: string | undefined,
	entryMethod?: "Manual" | "Automatic",
) => {
	return useQuery({
		queryKey: enrollmentId
			? QK_FOR_ENROLLMENT(enrollmentId, entryMethod)
			: ["event-instances", "noop"],
		queryFn: () => {
			if (!enrollmentId) {
				return Promise.reject(new Error("Missing enrollmentId"));
			}
			return eventInstanceService.listEventInstancesForEnrollment(
				enrollmentId,
				entryMethod,
			);
		},
		enabled: Boolean(enrollmentId),
	});
};

export const useReviewDetail = (
	enrollmentId: string | undefined,
	instanceId: string | undefined,
) => {
	return useQuery({
		queryKey:
			enrollmentId && instanceId
				? QK_DETAIL(enrollmentId, instanceId)
				: ["event-instances", "noop"],
		queryFn: () => {
			if (!enrollmentId || !instanceId) {
				return Promise.reject(new Error("Missing enrollmentId/instanceId"));
			}
			return eventInstanceService.getReviewDetail(enrollmentId, instanceId);
		},
		enabled: Boolean(enrollmentId && instanceId),
	});
};

export const useApproveEventInstance = (reviewerId: string) => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (args: {
			enrollmentId: string;
			instanceId: string;
			body: ApproveRequest;
		}) =>
			eventInstanceService.approveEventInstance(
				args.enrollmentId,
				args.instanceId,
				reviewerId,
				args.body,
			),
		onSuccess: (_, args) => {
			qc.invalidateQueries({ queryKey: QK_PENDING });
			qc.invalidateQueries({
				queryKey: QK_FOR_ENROLLMENT(args.enrollmentId),
			});
			qc.invalidateQueries({
				queryKey: QK_DETAIL(args.enrollmentId, args.instanceId),
			});
			// SIMS-5878: the participant profile tabs render the reviewed items
			// from their own query keys, so refresh them too (Clinical Info tab
			// -> "participant-input", Daily Journey tab -> "daily-journey").
			// Partial-match keys so every filter variant is invalidated.
			qc.invalidateQueries({
				queryKey: ["participant-input", args.enrollmentId],
			});
			qc.invalidateQueries({
				queryKey: ["daily-journey", args.enrollmentId],
			});
			// SIMS-4338: a review decision can move a submission into/out of the
			// "Input Submitted" sidebar feed (and change its badge count), so
			// refresh the feed + the sidebar counts. Partial-match keys cover
			// every coordinatorId variant.
			qc.invalidateQueries({
				queryKey: ["sims-activity-input-submitted"],
			});
			qc.invalidateQueries({ queryKey: ["sims-activity-sidebar"] });
		},
	});
};

export const useRejectEventInstance = (reviewerId: string) => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (args: {
			enrollmentId: string;
			instanceId: string;
			body: RejectRequest;
		}) =>
			eventInstanceService.rejectEventInstance(
				args.enrollmentId,
				args.instanceId,
				reviewerId,
				args.body,
			),
		onSuccess: (_, args) => {
			qc.invalidateQueries({ queryKey: QK_PENDING });
			qc.invalidateQueries({
				queryKey: QK_FOR_ENROLLMENT(args.enrollmentId),
			});
			qc.invalidateQueries({
				queryKey: QK_DETAIL(args.enrollmentId, args.instanceId),
			});
			// SIMS-5878: the participant profile tabs render the reviewed items
			// from their own query keys, so refresh them too (Clinical Info tab
			// -> "participant-input", Daily Journey tab -> "daily-journey").
			// Partial-match keys so every filter variant is invalidated.
			qc.invalidateQueries({
				queryKey: ["participant-input", args.enrollmentId],
			});
			qc.invalidateQueries({
				queryKey: ["daily-journey", args.enrollmentId],
			});
			// SIMS-4338: a review decision can move a submission into/out of the
			// "Input Submitted" sidebar feed (and change its badge count), so
			// refresh the feed + the sidebar counts. Partial-match keys cover
			// every coordinatorId variant.
			qc.invalidateQueries({
				queryKey: ["sims-activity-input-submitted"],
			});
			qc.invalidateQueries({ queryKey: ["sims-activity-sidebar"] });
		},
	});
};
